import { type PropsWithChildren, type ReactNode, type MouseEvent, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { cn, PaletteUtils } from '@shared/utils';
import { Button, Icon, Spinner, Text } from '../index';
import CloseIcon from '@assets/icons/close.svg';

export type ModalProps = PropsWithChildren & {
  className?: string;
  overlayClassName?: string;
  headerClassName?: string;
  mainClassName?: string;
  isOpen: boolean;
  onClose?: () => void;
  loading?: boolean;
  title?: ReactNode;
  width?: string;
  position?: 'top' | 'center';
  setAutoCloseSeconds?: number;
};

export const Modal = (props: ModalProps) => {
  const { isOpen, setAutoCloseSeconds, onClose } = props;

  useEffect(() => {
    if (!isOpen || !setAutoCloseSeconds) return;
    const timerId = setTimeout(() => onClose?.(), setAutoCloseSeconds * 1000);
    return () => clearTimeout(timerId);
  }, [isOpen, setAutoCloseSeconds, onClose]);

  const overlayMouseDownHandler = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) onClose?.();
  };

  if (!props.isOpen) return null;
  return createPortal(
    <div
      className={cn(
        'fixed inset-0 z-50 flex items-center justify-center bg-almost-dark-black p-4',
        props.position === 'top' && 'items-start pt-[5dvh]',
        props.overlayClassName,
      )}
      role='presentation'
      onMouseDown={overlayMouseDownHandler}
    >
      <div
        className={cn(
          'relative flex max-h-[90vh] min-w-[332px] flex-col overflow-y-auto p-6',
          PaletteUtils.isInsigniaPalette && 'bg-white dark:bg-eerie-black',
          PaletteUtils.isVcfcPalette && 'rounded-xl bg-ice dark:bg-soft-black',
          props.className,
        )}
        style={{ width: props.width }}
        role='dialog'
        aria-modal='true'
      >
        {(props.title || props.onClose) && (
          <header className={cn('mb-4 flex items-center justify-between gap-3', !props.title && 'justify-end', props.headerClassName)}>
            <Text tag='h3' size='2xl'>
              {props.title}
            </Text>
            {props.onClose && (
              <Button color='icon' onClick={props.onClose}>
                <Icon icon={<CloseIcon />} />
              </Button>
            )}
          </header>
        )}
        <main className={cn('flex-1 overflow-y-auto', props.mainClassName)}>{props.loading ? <Spinner /> : props.children}</main>
      </div>
    </div>,
    document.body,
  );
};
